import { useCallback, useMemo, useState, useEffect } from 'react'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiPost } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import MosSmartTable from '../common/MosSmartTable'
import MosSmartDialog from '../common/MosSmartDialog'
import NumericKeypad from '../common/NumericKeypad'
import LoadInstanceSelectionDialog from './LoadInstanceSelectionDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import AlertUI from '../../../generic/AlertUI'

// S3: lists the Raw Material References available in the general stock by reusing the SAME source
// and mechanism as the Raw Material Reference configuration table — `dataFetch` as a string +
// `pageable`, so the table's own `usePagination` builds the exact request the paginated endpoint
// expects (a hand-rolled `apiGet` with a guessed page/size/sort shape did NOT work). The load
// destination is fixed to the current MOS machine (`useMosInfo().device`); the operator only picks
// references and quantities — there is no destination selector here (that is the Raw Material
// Reference tab's entry point instead).
// S4: this is now a multi-reference load — the operator can set a quantity on any number of rows
// (a stepper per row, same look & feel as the "To Use" column of ResourceListWidget/components)
// before confirming; a single request loads every reference with quantity > 0 at once. The
// effectively loaded quantity is reported back per reference.
// S6: overshoot — when the exact quantity cannot be covered with whole instances, the backend loads
// more than requested (`overshoot: true`) for that reference and the user is warned about it.
//
// Assumption (simplification vs. the components widget): quantities here are expressed only in the
// reference's own unit — there is no second-unit toggle. Decimals are only allowed through the
// keypad, and only when `row.fractionable === true`; the +/- buttons always step by whole units.
//
// Instance selection: besides the quantity stepper (auto-pick, the backend chooses whole instances),
// the operator can open a per-reference picker and choose the exact general-stock instances to load.
// When a reference has instances selected, that selection drives its load line as
// `{ referenceId, instanceSelections: [{ instanceId, quantity? }] }` (partial per instance when the
// reference allows it — the picker enforces the granularity matrix) and its quantity stepper is
// disabled; references left on the stepper keep the pre-existing `{ referenceId, quantity }` line.
// Both kinds go in a single request; `instanceSelections` takes precedence over `quantity` server-side.
const LoadDialog = ({ open, onClose, onSuccess }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker } = useMosInfo()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [qtyByRefId, setQtyByRefId] = useState({})
  const [refInfoByRefId, setRefInfoByRefId] = useState({})
  const [instanceSelectionsByRefId, setInstanceSelectionsByRefId] = useState({})
  const [instanceDialog, setInstanceDialog] = useState({ open: false, referenceId: 0, referenceCode: '' })
  const [keypadInfo, setKeypadInfo] = useState({ open: false, id: 0, max: 0, val: 0, fractionable: false })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setQtyByRefId({})
      setRefInfoByRefId({})
      setInstanceSelectionsByRefId({})
      setInstanceDialog({ open: false, referenceId: 0, referenceCode: '' })
      setLoading(false)
      dismiss()
    }
  }, [open, dismiss])

  // Remembers the minimal info of a touched reference (code, availableQuantity, fractionable) so it
  // survives table pagination/search changes — needed later for the clamp and the overshoot message.
  const rememberRefInfo = useCallback((row) => {
    setRefInfoByRefId((prev) => ({
      ...prev,
      [row.id]: {
        code: row.code ?? '',
        availableQuantity: row.availableQuantity ?? 0,
        fractionable: row.fractionable === true
      }
    }))
  }, [])

  const incQty = useCallback((row) => {
    rememberRefInfo(row)
    const max = row.availableQuantity ?? 0
    setQtyByRefId((prev) => {
      const next = (prev[row.id] ?? 0) + 1
      return { ...prev, [row.id]: Math.min(next, max) }
    })
  }, [rememberRefInfo])

  const decQty = useCallback((row) => {
    rememberRefInfo(row)
    setQtyByRefId((prev) => {
      const next = Math.max((prev[row.id] ?? 0) - 1, 0)
      return { ...prev, [row.id]: next }
    })
  }, [rememberRefInfo])

  const openKeypad = useCallback((row) => {
    const max = row.availableQuantity ?? 0
    if (max <= 0) return
    rememberRefInfo(row)
    setKeypadInfo({ open: true, id: row.id, max, val: qtyByRefId[row.id] ?? 0, fractionable: row.fractionable === true })
  }, [rememberRefInfo, qtyByRefId])

  const handleKeypadAccept = useCallback((newVal) => {
    if (!keypadInfo.id) return
    let finalVal = Math.min(newVal, keypadInfo.max)
    finalVal = keypadInfo.fractionable ? Math.round(finalVal * 100) / 100 : Math.floor(finalVal)
    finalVal = Math.max(finalVal, 0)
    setQtyByRefId((prev) => ({ ...prev, [keypadInfo.id]: finalVal }))
  }, [keypadInfo])

  const openInstances = useCallback((row) => {
    rememberRefInfo(row)
    setInstanceDialog({ open: true, referenceId: row.id, referenceCode: row.code ?? '' })
  }, [rememberRefInfo])

  const closeInstances = useCallback(() => {
    setInstanceDialog((prev) => ({ ...prev, open: false }))
  }, [])

  // Instance selection and the quantity stepper are mutually exclusive per reference: picking
  // instances takes over the load line, so any auto-pick quantity for that reference is cleared;
  // clearing the selection (confirming with none) hands the reference back to the stepper.
  const handleConfirmInstances = useCallback((referenceId, selections) => {
    setInstanceSelectionsByRefId((prev) => {
      const next = { ...prev }
      if (selections.length > 0) next[referenceId] = selections
      else delete next[referenceId]
      return next
    })
    if (selections.length > 0) {
      setQtyByRefId((prev) => {
        if (!prev[referenceId]) return prev
        const next = { ...prev }
        delete next[referenceId]
        return next
      })
    }
  }, [])

  const submitLoad = async () => {
    const quantityLines = Object.entries(qtyByRefId)
      .filter(([referenceId, quantity]) => quantity > 0 && !((instanceSelectionsByRefId[referenceId] ?? []).length > 0))
      .map(([referenceId, quantity]) => ({ referenceId: Number(referenceId), quantity }))
    const instanceLines = Object.entries(instanceSelectionsByRefId)
      .filter(([, selections]) => (selections ?? []).length > 0)
      .map(([referenceId, selections]) => ({ referenceId: Number(referenceId), instanceSelections: selections }))
    const list = [...quantityLines, ...instanceLines]
    if (list.length === 0) {
      showAlert({ severity: 'warning', title: t_('Quantity must be greater than 0') })
      return
    }
    setLoading(true)
    try {
      const result = await apiPost('mos/machineStorage/' + worker.id + '/' + device.id + '/load', list)
      const lines = Array.isArray(result) ? result : []
      const overshootLines = lines.filter((line) => line.overshoot)
      if (overshootLines.length > 0) {
        const message = overshootLines
          .map((line) => {
            const info = refInfoByRefId[line.referenceId] ?? {}
            const loadedQuantity = line.loadedQuantity ?? 0
            const requestedQuantity = line.requestedQuantity ?? 0
            return t_('Code') + ': ' + info.code + ' — ' + t_(
              'Loaded {{loaded}} units instead of the {{requested}} units requested because whole instances could not cover the exact quantity',
              { loaded: loadedQuantity, requested: requestedQuantity }
            )
          })
          .join(' | ')
        showAlert({ severity: 'warning', title: t_('Loaded more than requested'), message })
      } else {
        const totalLoaded = lines.reduce((sum, line) => sum + (line.loadedQuantity ?? 0), 0)
        showAlert({ severity: 'success', title: t_('Material loaded'), message: t_('Loaded {{quantity}} units', { quantity: totalLoaded }) })
      }
      setQtyByRefId({})
      setRefInfoByRefId({})
      setInstanceSelectionsByRefId({})
      if (onSuccess) onSuccess()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  const columns = useMemo(() => [
    { name: t_('Code'), field: 'code', readOnly: true },
    { name: t_('Name'), field: 'name', readOnly: true },
    { name: t_('Available in general stock'), field: 'availableQuantity', readOnly: true, search: false },
    {
      name: t_('Quantity to load'),
      field: 'quantityToLoad',
      readOnly: true,
      search: false,
      sortable: false,
      render: (row) => {
        const max = row.availableQuantity ?? 0
        const lockedByInstances = (instanceSelectionsByRefId[row.id] ?? []).length > 0
        const val = max > 0 && !lockedByInstances ? (qtyByRefId[row.id] ?? 0) : 0
        const canEdit = max > 0 && !lockedByInstances
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              data-testid={'load-dialog-decrease-' + row.id}
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => decQty(row)}
              disabled={val <= 0 || !canEdit}
            >
              <RemoveCircleOutlineIcon fontSize='large' />
            </Button>
            <Box
              data-testid={'load-dialog-qty-box-' + row.id}
              sx={{
                bgcolor: 'background.default',
                px: 2,
                py: 1.25,
                borderRadius: 1,
                minWidth: 64,
                textAlign: 'center',
                cursor: canEdit ? 'pointer' : 'default',
                opacity: canEdit ? 1 : 0.38,
                '&:hover': {
                  bgcolor: canEdit ? 'action.hover' : 'background.default'
                }
              }}
              onClick={() => { if (canEdit) openKeypad(row) }}
            >
              <Typography variant='h6' sx={{ lineHeight: 1 }}>
                {val}
              </Typography>
            </Box>
            <Button
              data-testid={'load-dialog-increase-' + row.id}
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => incQty(row)}
              disabled={val >= max || !canEdit}
            >
              <AddCircleOutlineIcon fontSize='large' />
            </Button>
          </Box>
        )
      }
    },
    {
      name: t_('Instances'),
      field: 'instances',
      readOnly: true,
      search: false,
      sortable: false,
      render: (row) => {
        const count = (instanceSelectionsByRefId[row.id] ?? []).length
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              data-testid={'load-dialog-instances-' + row.id}
              aria-label={t_('View instances')}
              onClick={() => openInstances(row)}
              size='small'
            >
              <VisibilityIcon />
            </IconButton>
            {count > 0 && (
              <Typography variant='body2' color='text.secondary'>{count + ' ' + t_('items selected')}</Typography>
            )}
          </Box>
        )
      }
    }
  ], [t_, qtyByRefId, instanceSelectionsByRefId, incQty, decQty, openKeypad, openInstances])

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Load material') })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={submitLoad}
      disableButtons={loading}
    >
      {AlertElement}
      <NumericKeypad
        open={keypadInfo.open}
        onClose={() => setKeypadInfo((prev) => ({ ...prev, open: false }))}
        onAccept={handleKeypadAccept}
        initialValue={keypadInfo.val}
        title={t_('Quantity to load')}
        allowDecimals={keypadInfo.fractionable}
      />
      <LoadInstanceSelectionDialog
        open={instanceDialog.open}
        referenceId={instanceDialog.referenceId}
        referenceCode={instanceDialog.referenceCode}
        selectedInstanceSelections={instanceSelectionsByRefId[instanceDialog.referenceId] ?? []}
        onClose={closeInstances}
        onConfirm={(selections) => handleConfirmInstances(instanceDialog.referenceId, selections)}
      />
      <Stack spacing={2} sx={{ mt: 1 }}>
        <MosSmartTable
          columns={columns}
          dataFetch='rawMaterialsReference/paginated/realAvailable'
          tableTuneUniqueKey='machineStorageLoadReference'
          tablePageSize={10}
          showSearchHeader
          hideExport
          pageable
        />
      </Stack>
    </MosSmartDialog>
  )
}

export default LoadDialog
